import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import './App.css';
import iconTrans from './assets/icon_trans.png';
import icon from './assets/Noorvia-Icon.png';
// TODO: swap in a dedicated Noorvia Gold icon when one is available
import goldIcon from './assets/spiritual_journey_icon.PNG';
import PrivacyPolicy from './PrivacyPolicy';
import Terms from './Terms';
import SupportPage from './SupportPage';
import Marble from './Marble';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import InsightsIcon from '@mui/icons-material/Insights';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import ChecklistIcon from '@mui/icons-material/Checklist';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import ExploreIcon from '@mui/icons-material/Explore';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SchoolIcon from '@mui/icons-material/School';
import PaidIcon from '@mui/icons-material/Paid';
import StarIcon from '@mui/icons-material/Star';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import WidgetsIcon from '@mui/icons-material/Widgets';
import WaterDropIcon from '@mui/icons-material/WaterDrop';

const APP_STORE_URL = 'https://apps.apple.com/us/app/noorvia/id6761079711';

const PRAYER = {
  fajr:    { c1: '#667EEA', c2: '#764BA2' },
  sunrise: { c1: '#C850C0', c2: '#D63865' },
  dhuhr:   { c1: '#2575CE', c2: '#0077B6' },
  asr:     { c1: '#1DA561', c2: '#0D8B7A' },
  maghrib: { c1: '#E04E77', c2: '#D4880B' },
  isha:    { c1: '#30CFD0', c2: '#330867' },
};

const features = [
  {
    title: 'Prayer Times & Notifications',
    desc: 'Accurate prayer times based on your location with customizable calculation methods. Set flexible reminders so you never miss a prayer again.',
    Icon: AccessTimeIcon,
    palette: PRAYER.fajr,
  },
  {
    title: 'Quran Reader',
    desc: 'Arabic with multiple English translations, audio recitations from renowned reciters, adjustable playback speed, bookmarks, notes, and collections.',
    Icon: MenuBookIcon,
    palette: PRAYER.dhuhr,
  },
  {
    title: 'Prayer Logging & Insights',
    desc: 'Log daily prayers and track your consistency over time with detailed insights and analytics that celebrate your progress.',
    Icon: InsightsIcon,
    palette: PRAYER.asr,
  },
  {
    title: 'Dhikr Counter',
    desc: 'A focused dhikr counter with a curated collection of remembrances. Track your sessions and build a consistent habit of remembrance.',
    Icon: TouchAppIcon,
    palette: PRAYER.isha,
  },
  {
    title: 'Spiritual Checklist',
    desc: 'Set daily spiritual goals, track streaks, and earn milestones as you build consistency. Customize the checklist to your journey.',
    Icon: ChecklistIcon,
    palette: PRAYER.asr,
  },
  {
    title: 'Fasting Tracker',
    desc: 'Log fasts during Ramadan and throughout the year. Track Qada makeup fasts and access educational content on the rulings of fasting.',
    Icon: BedtimeIcon,
    palette: PRAYER.maghrib,
  },
  {
    title: 'Qibla Compass',
    desc: 'Find the direction of prayer anywhere in the world with a beautiful, accurate compass.',
    Icon: ExploreIcon,
    palette: PRAYER.dhuhr,
  },
  {
    title: 'Hadith Collection',
    desc: 'Browse hadith from Sahih al-Bukhari organized by book and topic. Bookmark, take notes, and organize favorites into folders.',
    Icon: AutoStoriesIcon,
    palette: PRAYER.fajr,
  },
  {
    title: '99 Names of Allah',
    desc: 'Learn and memorize the 99 Names with meanings, benefits, and study tools. Test yourself with quizzes and flashcards.',
    Icon: FavoriteIcon,
    palette: PRAYER.sunrise,
  },
  {
    title: 'Islamic Calendar',
    desc: 'View Hijri dates alongside Gregorian dates. Stay informed about upcoming Islamic events with adjustable Hijri offset.',
    Icon: CalendarMonthIcon,
    palette: PRAYER.isha,
  },
  {
    title: 'Learning Guides',
    desc: 'Step-by-step guides for Wudu, Ghusl, and Salah — perfect for new Muslims and anyone refreshing their knowledge.',
    Icon: SchoolIcon,
    palette: PRAYER.sunrise,
  },
  {
    title: 'Zakat Tracker',
    desc: 'Calculate and track your Zakat obligations with an easy-to-use calculator and a payment history log.',
    Icon: PaidIcon,
    palette: PRAYER.asr,
  },
  {
    title: 'Sunnah Prayer Tracking',
    desc: 'Track voluntary Sunnah prayers alongside obligatory ones to build a complete picture of your daily worship.',
    Icon: StarIcon,
    palette: PRAYER.fajr,
  },
  {
    title: 'Recitation Player',
    desc: 'Listen to curated Quran recitation playlists for peaceful listening and memorization practice.',
    Icon: HeadphonesIcon,
    palette: PRAYER.dhuhr,
  },
  {
    title: 'Home Screen Widgets',
    desc: 'Beautiful widgets that keep faith visible — prayer times, verse of the day, Allah’s Names, and more.',
    Icon: WidgetsIcon,
    palette: PRAYER.maghrib,
  },
  {
    title: 'Purity Tracker',
    desc: 'Track menstruation and ritual purity periods for accurate prayer and fasting obligations.',
    Icon: WaterDropIcon,
    palette: PRAYER.isha,
  },
];

const designPillars = [
  'Dark mode with a beautiful glass aesthetic',
  'Customizable accent colors and prayer card styles',
  'No ads, no tracking, no data collection',
  'Synced privately through your personal iCloud',
  'Works offline for core features',
];

const gold = [
  'Prayer logging',
  'Quran tracking',
  'Fasting tracker',
  'Zakat tracker',
  'Sunnah tracking',
  'Spiritual checklist',
  'Detailed insights',
  'Additional accent colors',
  'Countdown skins',
  'Prayer icon animations',
  'Border effects',
  'Compass styles',
];

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      requestAnimationFrame(() => {
        const el = document.getElementById(hash.slice(1));
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function Nav() {
  return (
    <nav className="nav">
      <Link to="/" className="nav-brand">
        <img src={icon} alt="" className="nav-icon" />
        <span>Noorvia</span>
      </Link>
      <div className="nav-links">
        <Link to="/#features">Features</Link>
        <Link to="/#privacy">Privacy</Link>
        <Link to="/#pricing">Pricing</Link>
      </div>
      <a
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-primary nav-cta"
      >
        Download
      </a>
    </nav>
  );
}

const SPARKLES = [
  { a: 12,  d: 210 },
  { a: 47,  d: 175 },
  { a: 82,  d: 220 },
  { a: 118, d: 185 },
  { a: 152, d: 215 },
  { a: 188, d: 180 },
  { a: 222, d: 205 },
  { a: 258, d: 175 },
  { a: 292, d: 220 },
  { a: 328, d: 190 },
];

function Hero() {
  const [bursts, setBursts] = useState(0);
  const triggerBurst = () => setBursts((b) => b + 1);
  return (
    <section className="hero" id="top">
      <div className="hero-orb hero-orb-blue" aria-hidden="true" />
      <div className="hero-orb hero-orb-purple" aria-hidden="true" />
      <div className="hero-grid">
        <div className="hero-copy">
          <span className="eyebrow">Your Islamic Companion</span>
          <h1 className="hero-title">
            Faith,<br />
            <span className="gradient-text">beautifully kept.</span>
          </h1>
          <p className="hero-sub">
            Noorvia helps you stay connected to your faith with prayer tracking, Quran reading, dhikr, fasting logs, and more — all beautifully designed and completely private.
          </p>
          <div className="hero-actions">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              Download for iPhone
            </a>
            <a href="#features" className="btn btn-ghost">Explore features</a>
          </div>
          <div className="hero-meta">
            <span><strong>16+</strong> tools</span>
            <span><strong>Private</strong> by design</span>
            <span><strong>Offline</strong> ready</span>
          </div>
        </div>
        <div className="hero-art">
          <div className="hero-glow" aria-hidden="true" />
          <div className="hero-candle" aria-hidden="true" />
          {bursts > 0 && (
            <div key={bursts} className="hero-sparkles" aria-hidden="true">
              {SPARKLES.map((s, i) => (
                <span
                  key={i}
                  className="sparkle"
                  style={{
                    '--angle': `${s.a}deg`,
                    '--dist': `${s.d}px`,
                  }}
                />
              ))}
            </div>
          )}
          <img
            src={iconTrans}
            alt="Noorvia app icon"
            className="hero-icon"
            onClick={triggerBurst}
          />
        </div>
      </div>
    </section>
  );
}

function Features() {
  return (
    <section className="section" id="features">
      <div className="section-head">
        <span className="eyebrow">Everything in one place</span>
        <h2 className="section-title">
          A complete companion for <span className="gradient-text">daily worship.</span>
        </h2>
        <p className="section-lede">
          Whether you’re tracking prayers, reading Quran, counting dhikr, or learning the Names of Allah, Noorvia brings everything together — beautifully.
        </p>
      </div>
      <div className="feature-grid">
        {features.map((f) => (
          <article key={f.title} className="feature-card">
            <Marble Icon={f.Icon} c1={f.palette.c1} c2={f.palette.c2} />
            <h3 className="feature-title">{f.title}</h3>
            <p className="feature-desc">{f.desc}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function Privacy() {
  return (
    <section className="section section-privacy" id="privacy">
      <div className="privacy-card">
        <span className="eyebrow">Designed with care</span>
        <h2 className="section-title">
          Private <span className="gradient-text">by design.</span>
        </h2>
        <p className="section-lede">
          Your spiritual life is yours alone. Noorvia is built to be respectful, calm, and out of the way.
        </p>
        <ul className="pillar-list">
          {designPillars.map((p) => (
            <li key={p} className="pillar">
              <span className="pillar-dot" aria-hidden="true" />
              {p}
            </li>
          ))}
        </ul>
        <Link to="/privacy-policy" className="privacy-link">Read the full Privacy Policy →</Link>
      </div>
    </section>
  );
}

function Pricing() {
  return (
    <section className="section" id="pricing">
      <div className="section-head">
        <span className="eyebrow">In-app purchase</span>
        <h2 className="section-title">
          Unlock the <span className="gradient-text">full experience.</span>
        </h2>
        <p className="section-lede">
          Core features are free. One optional subscription unlocks everything else.
        </p>
      </div>
      <div className="plan-grid">
        <article className="plan-card plan-card-feature">
          <img src={goldIcon} alt="" className="plan-icon" />
          <div className="plan-header">
            <h3 className="plan-name">Noorvia Gold</h3>
            <span className="plan-price">$2.99<span className="plan-period">/month</span></span>
          </div>
          <p className="plan-tagline">Go deeper into your practice — and make Noorvia yours.</p>
          <ul className="plan-list">
            {gold.map((p) => (
              <li key={p}><span className="check" aria-hidden="true" />{p}</li>
            ))}
          </ul>
          <p className="plan-note">
            Auto-renews monthly. Cancel anytime in your Apple ID settings.
          </p>
        </article>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="section section-cta" id="download">
      <div className="cta-orb" aria-hidden="true" />
      <h2 className="cta-title">
        Begin your <span className="gradient-text">journey.</span>
      </h2>
      <p className="cta-sub">
        Download Noorvia and bring your daily worship into one beautifully designed companion.
      </p>
      <a
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-primary btn-lg"
      >
        Download on the App Store
      </a>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="footer-row">
        <div className="footer-brand">
          <img src={icon} alt="" className="footer-icon" />
          <span>Noorvia</span>
        </div>
        <div className="footer-links">
          <Link to="/#features">Features</Link>
          <Link to="/#pricing">Pricing</Link>
          <Link to="/support">Support</Link>
          <Link to="/privacy-policy">Privacy Policy</Link>
          <Link to="/terms">Terms</Link>
          <a
            href="https://www.patreon.com/c/MukuwaBaffoe"
            target="_blank"
            rel="noopener noreferrer"
          >
            Patreon
          </a>
        </div>
      </div>
      <p className="footer-fine">
        &copy; 2026 Mukuwa Baffoe. All rights reserved.
      </p>
    </footer>
  );
}

function Home() {
  return (
    <main>
      <Hero />
      <Features />
      <Privacy />
      <Pricing />
      <CTA />
    </main>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <div className="app">
        <Nav />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/support" element={<SupportPage />} />
        </Routes>
        <Footer />
      </div>
    </BrowserRouter>
  );
}

export default App;
